/**
 * RESEND EMAIL SERVICE
 *
 * Comprehensive email service using Resend API for:
 * - Email verification codes
 * - Account security notifications
 * - Newsletter and bulk email functionality
 * - Password reset emails
 * - Account change confirmations
 *
 * IMPORTANT: This service should ONLY be used on the server-side.
 * For browser-side email sending, use the /api/send-email endpoint.
 * The Resend API key should NEVER be exposed to the client.
 */

import { Resend } from 'resend';
import { supabase, getServiceRoleClient } from './supabase';
import { getSessionHeaders } from './serverSession';
import crypto from 'crypto';

// Server-side Supabase client for dedup - uses centralized singleton
function getServerSupabaseClient() {
 if (typeof window !== 'undefined') return null;
 try {
 return getServiceRoleClient();
 } catch {
 return null;
 }
}

// Check if running in browser - Resend should NEVER be initialized client-side
const IS_BROWSER = typeof window !== 'undefined';

// Environment configuration - Only use non-VITE prefixed keys on server
// SECURITY: VITE_ prefixed keys are exposed to client, so we ONLY use RESEND_API_KEY (server-only)
const RESEND_API_KEY = IS_BROWSER ? null : (process.env.RESEND_API_KEY || null);
const RESEND_FROM_EMAIL = import.meta.env.VITE_RESEND_FROM_EMAIL || 'verify@aureus.africa';
const RESEND_FROM_NAME = import.meta.env.VITE_RESEND_FROM_NAME || 'Aureus Alliance Holdings';

// Initialize Resend client - ONLY on server-side
let resend: Resend | null = null;

if (!IS_BROWSER) {
 try {
 if (RESEND_API_KEY) {
 resend = new Resend(RESEND_API_KEY);
 // v7.3.319: Silent initialization — no console output
 }
 } catch (_) {
 // Silent failure — service will report not configured when called
 }
}

export interface EmailVerificationData {
 email: string;
 code: string;
 purpose: 'registration' | 'account_update' | 'withdrawal' | 'password_reset' | 'telegram_connection';
 userName?: string;
 expiryMinutes?: number;
}

export interface BulkEmailData {
 recipients: string[];
 subject: string;
 htmlContent: string;
 textContent?: string;
 templateData?: Record<string, any>;
}

export interface WelcomeEmailData {
 email: string;
 fullName: string;
 username: string;
 sponsorUsername?: string;
}

export interface ConversionNotificationData {
 email: string;
 fullName: string;
 usdtAmount: number;
 sharesReceived: number;
 sharePrice: number;
 transactionId: string;
}

export interface SharePurchaseConfirmationData {
 email: string;
 fullName: string;
 sharesPurchased: number;
 totalAmount: number;
 sharePrice: number;
 phaseName: string;
 transactionId: string;
}

export interface CommissionEarnedData {
 email: string;
 fullName: string;
 usdtCommission: number;
 shareCommission: number;
 referredUserName: string;
 purchaseAmount: number;
 transactionId: string;
}

export interface WithdrawalNotificationData {
 email: string;
 fullName: string;
 withdrawalAmount: number;
 currency: string;
 status: 'requested' | 'approved' | 'completed' | 'rejected';
 walletAddress?: string;
 transactionId: string;
}

export interface ShareTransferNotificationData {
 email: string;
 fullName: string;
 type: 'sender' | 'recipient';
 shares: number;
 transferFee: number;
 senderName?: string;
 recipientName?: string;
 transactionId: string;
}

export interface NewReferralNotificationData {
 email: string;
 fullName: string;
 newUserName: string;
 newUserEmail: string;
 registrationDate: string;
 referralLink?: string;
}

export interface MigrationConfirmationData {
 email: string;
 fullName: string;
 username: string;
 telegramUsername: string;
}

export interface EmailDeliveryResult {
 success: boolean;
 messageId?: string;
 error?: string;
 deliveryStatus?: 'sent' | 'delivered' | 'bounced' | 'failed';
}

// Type alias for backward compatibility
export type EmailResult = EmailDeliveryResult;

export interface NewsletterSubscription {
 email: string;
 categories: string[];
 preferences: Record<string, any>;
 unsubscribeToken: string;
}

class ResendEmailService {
 private isConfigured(): boolean {
 // On browser, we proxy through API, so always return true
 if (IS_BROWSER) return true;
 return resend !== null && !!RESEND_API_KEY;
 }

 /**
 * Proxy email send through API (for browser-side calls)
 */
 private async sendViaAPI(emailType: string, emailData: any): Promise<{ success: boolean; messageId?: string; error?: string }> {
 try {
 // Get session headers for authenticated API calls
 const sessionHeaders = getSessionHeaders();

 // Verification emails use a different endpoint
 if (emailType === 'verification') {
 const response = await fetch('/api/send-verification-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify(emailData)
 });
 const result = await response.json();
 if (!response.ok) {
 return { success: false, error: result.error || 'API request failed' };
 }
 return { success: result.success !== false, messageId: result.messageId, error: result.error };
 }

 // All other emails go through centralized send-email endpoint
 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({ emailType, data: emailData })
 });
 const result = await response.json();
 if (!response.ok) {
 return { success: false, error: result.error || 'API request failed' };
 }
 return { success: result.success, messageId: result.messageId, error: result.error };
 } catch (error) {
 console.error('API proxy error:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Network error' };
 }
 }

 /**
 * Generate a deduplication key for an email
 * Uses recipient + normalized subject (removes variable amounts/numbers)
 */
 private generateDedupKey(to: string, subject: string): string {
 // Normalize subject by removing variable numbers/amounts
 // "Commission Earned - $15.00 USDT + 3.00 Shares" -> "Commission Earned - USDT + Shares"
 // "Share Purchase Confirmed - 0.05 Shares" -> "Share Purchase Confirmed - Shares"
 const normalizedSubject = subject
 .replace(/\$[\d,]+\.?\d*/g, '') // Remove dollar amounts
 .replace(/[\d,]+\.?\d*/g, '') // Remove all numbers
 .replace(/\s+/g, ' ') // Collapse whitespace
 .trim();

 const keyData = `${to}:${normalizedSubject}`;
 return crypto.createHash('md5').update(keyData).digest('hex');
 }

 /**
 * Check if email was recently sent (deduplication)
 * Returns true if this is a duplicate (should skip), false if OK to send
 */
 private async isDuplicateEmail(to: string, subject: string): Promise<boolean> {
 if (IS_BROWSER) return false; // Browser uses API which has its own dedup

 try {
 // Use server-side client - NO getServiceRoleClient() to avoid browser exposure
 const serviceClient = getServerSupabaseClient();
 if (!serviceClient) {
 console.warn('[EmailService] No server client available for dedup');
 return false; // Fail open - allow email
 }

 const dedupKey = this.generateDedupKey(to, subject);

 console.log(`[EmailService] Checking dedup for ${to}: ${subject} -> key: ${dedupKey}`);

 // Try to insert the dedup record - if it already exists, this is a duplicate
 const { error } = await serviceClient
 .from('sent_email_dedup')
 .insert({
 dedup_key: dedupKey,
 email_type: 'generic',
 recipient_email: to
 });

 if (error) {
 if (error.code === '23505') { // Unique violation - duplicate
 console.log(`[EmailService] DUPLICATE BLOCKED for ${to}: ${subject}`);
 return true;
 }
 // Other errors - allow email to be sent (fail open)
 console.warn('[EmailService] Dedup check error:', error.message);
 return false;
 }

 console.log(`[EmailService] New email allowed for ${to}: ${subject}`);
 return false; // Not a duplicate, OK to send
 } catch (err) {
 console.warn('[EmailService] Dedup check exception:', err);
 return false; // On exception, allow the email (fail open)
 }
 }

 /**
 * Generic send email method with deduplication
 */
 async sendEmail(data: { to: string; subject: string; html: string; text?: string; skipDedup?: boolean }): Promise<{ success: boolean; messageId?: string; error?: string; skipped?: boolean }> {
 // Proxy through API if running in browser
 if (IS_BROWSER) {
 return this.sendViaAPI('custom', { email: data.to, subject: data.subject, htmlContent: data.html, textContent: data.text });
 }

 if (!this.isConfigured()) {
 console.error('Resend service not configured');
 return { success: false, error: 'Email service not configured' };
 }

 // Check for duplicate email (unless explicitly skipped)
 if (!data.skipDedup) {
 const isDuplicate = await this.isDuplicateEmail(data.to, data.subject);
 if (isDuplicate) {
 console.log(`[EmailService] Skipping duplicate email to ${data.to}`);
 return { success: true, skipped: true, messageId: undefined };
 }
 }

 try {
 const result = await resend!.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [data.to],
 subject: data.subject,
 html: data.html,
 text: data.text || data.html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 tags: [
 { name: 'category', value: 'notification' }
 ]
 });

 if (result.error) {
 console.error('Resend API error:', result.error);
 return { success: false, error: result.error.message };
 }

 console.log(`Email sent to ${data.to}`);
 return {
 success: true,
 messageId: result.data?.id
 };
 } catch (error) {
 console.error('Failed to send email:', error);
 return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
 }
 }

 /**
 * Send email verification code
 */
 async sendVerificationCode(data: EmailVerificationData): Promise<EmailDeliveryResult> {
 // Proxy through API if running in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('verification', {
 email: data.email,
 code: data.code,
 purpose: data.purpose,
 userName: data.userName,
 expiryMinutes: data.expiryMinutes
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!this.isConfigured()) {
 console.error('Resend service not configured');
 return { success: false, error: 'Email service not configured' };
 }

 try {
 const { email, code, purpose, userName, expiryMinutes = 15 } = data;

 // Get user ID for preference checking
 const { data: userData } = await supabase
 .from('users')
 .select('id')
 .eq('email', email)
 .single();

 const userId = userData?.id;

 // Generate email content based on purpose
 const emailContent = this.generateVerificationEmailContent(code, purpose, userName, expiryMinutes);

 const result = await resend!.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject: emailContent.subject,
 html: emailContent.html,
 text: emailContent.text,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 tags: [
 { name: 'category', value: 'verification' },
 { name: 'purpose', value: purpose }
 ]
 });

 if (result.error) {
 console.error('Resend API error:', result.error);
 return { success: false, error: result.error.message };
 }

 // Log email delivery
 await this.logEmailDelivery({
 email,
 // emailType: `verification_${purpose}`,
 resendMessageId: result.data?.id,
 status: 'sent',
 subject: emailContent.subject,
 userId
 });

 console.log(`Verification email sent to ${email} (${purpose})`);
 return { 
 success: true, 
 messageId: result.data?.id,
 deliveryStatus: 'sent'
 };

 } catch (error) {
 console.error('Error sending verification email:', error);
 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
 }

 /**
 * Send password reset email
 */
 async sendPasswordReset(email: string, resetToken: string, userName?: string): Promise<EmailDeliveryResult> {
 if (!this.isConfigured()) {
 return { success: false, error: 'Email service not configured' };
 }

 try {
 const resetLink = `${window.location.origin}/reset-password?token=${resetToken}`;
 const emailContent = this.generatePasswordResetEmailContent(resetLink, userName);

 const result = await resend!.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject: emailContent.subject,
 html: emailContent.html,
 text: emailContent.text,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 tags: [
 { name: 'category', value: 'password_reset' }
 ]
 });

 if (result.error) {
 return { success: false, error: result.error.message };
 }

 await this.logEmailDelivery({
 email,
 // emailType: 'password_reset',
 resendMessageId: result.data?.id,
 status: 'sent',
 subject: emailContent.subject
 });

 return { success: true, messageId: result.data?.id };

 } catch (error) {
 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
 }

 /**
 * Send account change notification
 */
 async sendAccountChangeNotification(
 email: string, 
 changes: Record<string, any>, 
 userName?: string
 ): Promise<EmailDeliveryResult> {
 if (!this.isConfigured()) {
 return { success: false, error: 'Email service not configured' };
 }

 try {
 const emailContent = this.generateAccountChangeEmailContent(changes, userName);

 const result = await resend!.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject: emailContent.subject,
 html: emailContent.html,
 text: emailContent.text,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 tags: [
 { name: 'category', value: 'account_change' }
 ]
 });

 if (result.error) {
 return { success: false, error: result.error.message };
 }

 await this.logEmailDelivery({
 email,
 // emailType: 'account_change',
 resendMessageId: result.data?.id,
 status: 'sent',
 subject: emailContent.subject
 });

 return { success: true, messageId: result.data?.id };

 } catch (error) {
 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
 }

 /**
 * Send bulk email (newsletter/announcements)
 */
 async sendBulkEmail(data: BulkEmailData): Promise<EmailDeliveryResult[]> {
 if (!this.isConfigured()) {
 return [{ success: false, error: 'Email service not configured' }];
 }

 const results: EmailDeliveryResult[] = [];
 const { recipients, subject, htmlContent, textContent } = data;

 // Send in batches to avoid rate limits
 const batchSize = 50;
 for (let i = 0; i < recipients.length; i += batchSize) {
 const batch = recipients.slice(i, i + batchSize);

 try {
 const result = await resend!.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: batch,
 subject,
 html: htmlContent,
 text: textContent || this.stripHtml(htmlContent),
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 tags: [
 { name: 'category', value: 'bulk_email' },
 { name: 'batch', value: `${Math.floor(i / batchSize) + 1}` }
 ]
 });

 if (result.error) {
 results.push({ success: false, error: result.error.message });
 } else {
 results.push({ success: true, messageId: result.data?.id });

 // Log bulk email delivery
 for (const email of batch) {
 await this.logEmailDelivery({
 email,
 // emailType: 'bulk_email',
 resendMessageId: result.data?.id,
 status: 'sent',
 subject
 });
 }
 }

 // Rate limiting delay
 await new Promise(resolve => setTimeout(resolve, 1000));

 } catch (error) {
 results.push({ 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 });
 }
 }

 return results;
 }

 /**
 * Generate verification email content
 */
 private generateVerificationEmailContent(
 code: string, 
 purpose: string, 
 userName?: string,
 expiryMinutes: number = 15
 ) {
 const greeting = userName ? `Hello ${userName}` : 'Hello';
 const purposeText = {
 registration: 'complete your account registration',
 account_update: 'confirm your account changes',
 withdrawal: 'authorize your withdrawal request',
 password_reset: 'reset your password',
 telegram_connection: 'connect your Telegram account'
 }[purpose] || 'verify your email';

 const subject = `Your Aureus Alliance verification code: ${code}`;

 const html = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <title>Email Verification</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
 <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="text-align: center; margin-bottom: 30px;">
 <h1 style="color: #D4AF37;">Aureus Alliance Holdings</h1>
 </div>
 
 <h2>Email Verification Required</h2>
 
 <p>${greeting},</p>
 
 <p>You need to verify your email address to ${purposeText}.</p>
 
 <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0;">
 <h3 style="margin: 0; color: #D4AF37;">Your Verification Code</h3>
 <div style="font-size: 32px; font-weight: bold; letter-spacing: 8px; margin: 15px 0; color: #333;">
 ${code}
 </div>
 <p style="margin: 0; color: #666; font-size: 14px;">
 This code expires in ${expiryMinutes} minutes
 </p>
 </div>
 
 <p><strong>Security Notice:</strong></p>
 <ul>
 <li>Never share this code with anyone</li>
 <li>Aureus Alliance will never ask for this code via phone or email</li>
 <li>If you didn't request this verification, please ignore this email</li>
 </ul>
 
 <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;">
 
 <p style="font-size: 12px; color: #666; text-align: center;">
 This email was sent by Aureus Alliance Holdings. If you have questions, 
 please contact our support team.
 </p>
 </div>
 </body>
 </html>
 `;

 const text = `
 Aureus Alliance Holdings - Email Verification
 
 ${greeting},
 
 You need to verify your email address to ${purposeText}.
 
 Your verification code: ${code}
 
 This code expires in ${expiryMinutes} minutes.
 
 Security Notice:
 - Never share this code with anyone
 - Aureus Alliance will never ask for this code via phone or email
 - If you didn't request this verification, please ignore this email
 
 This email was sent by Aureus Alliance Holdings.
 `;

 return { subject, html, text };
 }

 /**
 * Generate password reset email content
 */
 private generatePasswordResetEmailContent(resetLink: string, userName?: string) {
 const greeting = userName ? `Hello ${userName}` : 'Hello';
 
 const subject = 'Reset your Aureus Alliance password';

 const html = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <title>Password Reset</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
 <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="text-align: center; margin-bottom: 30px;">
 <h1 style="color: #D4AF37;">Aureus Alliance Holdings</h1>
 </div>
 
 <h2>Password Reset Request</h2>
 
 <p>${greeting},</p>
 
 <p>We received a request to reset your password. Click the button below to create a new password:</p>
 
 <div style="text-align: center; margin: 30px 0;">
 <a href="${resetLink}" 
 style="background: #D4AF37; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; display: inline-block; font-weight: bold;">
 Reset Password
 </a>
 </div>
 
 <p>If the button doesn't work, copy and paste this link into your browser:</p>
 <p style="word-break: break-all; color: #666;">${resetLink}</p>
 
 <p><strong>Security Notice:</strong></p>
 <ul>
 <li>This link expires in 24 hours</li>
 <li>If you didn't request this reset, please ignore this email</li>
 <li>Your password will not change until you create a new one</li>
 </ul>
 
 <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;">
 
 <p style="font-size: 12px; color: #666; text-align: center;">
 This email was sent by Aureus Alliance Holdings. If you have questions, 
 please contact our support team.
 </p>
 </div>
 </body>
 </html>
 `;

 const text = `
 Aureus Alliance Holdings - Password Reset
 
 ${greeting},
 
 We received a request to reset your password. 
 
 Reset your password by visiting this link: ${resetLink}
 
 Security Notice:
 - This link expires in 24 hours
 - If you didn't request this reset, please ignore this email
 - Your password will not change until you create a new one
 
 This email was sent by Aureus Alliance Holdings.
 `;

 return { subject, html, text };
 }

 /**
 * Generate account change notification content
 */
 private generateAccountChangeEmailContent(changes: Record<string, any>, userName?: string) {
 const greeting = userName ? `Hello ${userName}` : 'Hello';
 const changesList = Object.entries(changes)
 .map(([field, value]) => `<li><strong>${field}:</strong> ${value}</li>`)
 .join('');

 const subject = 'Your Aureus Alliance account has been updated';

 const html = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <title>Account Update Notification</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
 <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="text-align: center; margin-bottom: 30px;">
 <h1 style="color: #D4AF37;">Aureus Alliance Holdings</h1>
 </div>
 
 <h2>Account Update Notification</h2>
 
 <p>${greeting},</p>
 
 <p>Your account information has been successfully updated. Here are the changes:</p>
 
 <ul style="background: #f8f9fa; padding: 20px; border-radius: 8px;">
 ${changesList}
 </ul>
 
 <p><strong>Security Notice:</strong></p>
 <ul>
 <li>If you didn't make these changes, please contact support immediately</li>
 <li>Review your account regularly for unauthorized changes</li>
 <li>Keep your login credentials secure</li>
 </ul>
 
 <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;">
 
 <p style="font-size: 12px; color: #666; text-align: center;">
 This email was sent by Aureus Alliance Holdings. If you have questions, 
 please contact our support team.
 </p>
 </div>
 </body>
 </html>
 `;

 const text = `
 Aureus Alliance Holdings - Account Update
 
 ${greeting},
 
 Your account information has been successfully updated.
 
 Changes made:
 ${Object.entries(changes).map(([field, value]) => `- ${field}: ${value}`).join('\n')}
 
 Security Notice:
 - If you didn't make these changes, please contact support immediately
 - Review your account regularly for unauthorized changes
 - Keep your login credentials secure
 
 This email was sent by Aureus Alliance Holdings.
 `;

 return { subject, html, text };
 }

 /**
 * Log email delivery to database (using existing email_delivery_log table)
 */
 private async logEmailDelivery(data: {
 email: string;
 emailType?: string;
 resendMessageId?: string;
 status: string;
 userId?: number;
 subject?: string;
 }) {
 try {
 await supabase
 .from('email_delivery_log')
 .insert({
 user_id: data.userId,
 email_address: data.email, // Note: existing table uses 'email_address'
 email_type: data.emailType || 'general',
 resend_message_id: data.resendMessageId,
 delivery_status: data.status, // Note: existing table uses 'delivery_status'
 subject: data.subject,
 created_at: new Date().toISOString(),
 updated_at: new Date().toISOString()
 });
 } catch (error) {
 console.error('Error logging email delivery:', error);
 }
 }

 /**
 * Send welcome email to new users
 */
 async sendWelcomeEmail(data: WelcomeEmailData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - welcome email not sent');
 return {
 success: false,
 error: 'Email service not configured'
 };
 }

 try {
 const subject = `Welcome to Aureus Alliance Holdings, ${data.fullName}!`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">Welcome to Aureus Alliance Holdings!</h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Hello ${data.fullName},</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 Welcome to Aureus Alliance Holdings! Your account has been successfully created with username: <strong style="color: #d4af37;">${data.username}</strong>
 </p>

 ${data.sponsorUsername ? `
 <p style="line-height: 1.6; margin-bottom: 15px;">
 You've been sponsored by: <strong style="color: #d4af37;">${data.sponsorUsername}</strong>
 </p>
 ` : ''}

 <p style="line-height: 1.6; margin-bottom: 15px;">
 You can now access your dashboard to:
 </p>
 <ul style="line-height: 1.8; margin-bottom: 20px; padding-left: 20px;">
 <li>Purchase shares in our gold mining operations</li>
 <li>Complete your KYC verification</li>
 <li>Access your share certificates</li>
 <li>Track your portfolio</li>
 <li>View operational updates and gallery</li>
 </ul>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/dashboard" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 Access Your Dashboard
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
Welcome to Aureus Alliance Holdings!

Hello ${data.fullName},

Your account has been successfully created with username: ${data.username}
${data.sponsorUsername ? `You've been sponsored by: ${data.sponsorUsername}` : ''}

You can now access your dashboard to:
- Purchase shares in our gold mining operations
- Complete your KYC verification
- Access your share certificates
- Track your portfolio
- View operational updates and gallery

Access your dashboard: https://aureus.africa/dashboard

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'welcome'
 });

 console.log('Welcome email sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending welcome email:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send share purchase confirmation email
 */
 async sendSharePurchaseConfirmation(data: SharePurchaseConfirmationData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - share purchase confirmation not sent');
 return {
 success: false,
 error: 'Email service not configured'
 };
 }

 try {
 // SMART share formatting: use 2 decimals for normal amounts, 8 decimals for micro-fractions
 const formatSharesSmartly = (shares: number): string => {
 if (shares >= 0.01) {
 return shares.toFixed(2); // Normal: "3.00", "0.15"
 } else if (shares > 0) {
 return shares.toFixed(8); // Micro-fractions: "0.00037500"
 } else {
 return "0";
 }
 };
 const formattedShares = formatSharesSmartly(data.sharesPurchased);
 const subject = `Share Purchase Confirmed - ${formattedShares} Shares Purchased`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">Share Purchase Confirmed!</h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Congratulations ${data.fullName}!</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 Your share purchase has been successfully processed and confirmed. Welcome to the Aureus Alliance Holdings family!
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">Purchase Details</h3>
 <table style="width: 100%; color: #ffffff;">
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Investment Phase:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.phaseName}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Shares Purchased:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #d4af37;">${formattedShares}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Share Price:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">$${data.sharePrice.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Total Investment:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">$${data.totalAmount.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0;">Transaction ID:</td>
 <td style="padding: 8px 0; text-align: right; font-family: monospace; font-size: 12px;">${data.transactionId}</td>
 </tr>
 </table>
 </div>

 <p style="line-height: 1.6; margin-bottom: 15px;">
 Your shares are now active in your portfolio.
 </p>

 <div style="background-color: #0f3460; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #d4af37;">
 <h4 style="color: #d4af37; margin-top: 0; margin-bottom: 10px;">What's Next?</h4>
 <ul style="margin: 0; padding-left: 20px; color: #ffffff;">
 <li style="margin-bottom: 8px;">View your shares in your dashboard portfolio</li>
 <li style="margin-bottom: 8px;">Download your share certificate (available after KYC completion)</li>
 <li style="margin-bottom: 8px;">Track mining progress</li>
 <li style="margin-bottom: 8px;">Refer friends to earn 10% USDT + automatic 5% share purchase on direct referrals</li>
 </ul>
 </div>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/dashboard" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block; margin-right: 10px;">
 View Dashboard
 </a>
 <a href="https://aureus.africa/dashboard#portfolio" style="background-color: #0f3460; color: #ffffff; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 View Portfolio
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
Share Purchase Confirmed!

Congratulations ${data.fullName}!

Your share purchase has been successfully processed and confirmed. Welcome to the Aureus Alliance Holdings family!

Purchase Details:
- Investment Phase: ${data.phaseName}
- Shares Purchased: ${data.sharesPurchased}
- Share Price: $${data.sharePrice.toFixed(2)}
- Total Investment: $${data.totalAmount.toFixed(2)}
- Transaction ID: ${data.transactionId}

Your shares are now active in your portfolio.

What's Next?
- View your shares in your dashboard portfolio
- Download your share certificate (available after KYC completion)
- Track mining progress
- Refer friends to earn 10% USDT + automatic 5% share purchase on direct referrals

View your dashboard: https://aureus.africa/dashboard

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'share_purchase'
 });

 console.log('Share purchase confirmation sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending share purchase confirmation:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send USDT to Shares conversion notification
 */
 async sendConversionNotification(data: ConversionNotificationData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - conversion notification not sent');
 return {
 success: false,
 error: 'Email service not configured'
 };
 }

 try {
 const subject = `USDT to Shares Conversion Completed - ${data.sharesReceived} Shares Received`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">Conversion Completed Successfully!</h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Hello ${data.fullName},</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 Your USDT to Shares conversion has been completed successfully!
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">Conversion Details</h3>
 <table style="width: 100%; color: #ffffff;">
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">USDT Converted:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">$${data.usdtAmount.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Share Price:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">$${data.sharePrice.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Shares Received:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #d4af37;">${data.sharesReceived}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0;">Transaction ID:</td>
 <td style="padding: 8px 0; text-align: right; font-family: monospace; font-size: 12px;">${data.transactionId}</td>
 </tr>
 </table>
 </div>

 <p style="line-height: 1.6; margin-bottom: 15px;">
 Your new shares have been added to your commission balance and are now available in your dashboard.
 </p>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/dashboard" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 View Your Dashboard
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
USDT to Shares Conversion Completed

Hello ${data.fullName},

Your USDT to Shares conversion has been completed successfully!

Conversion Details:
- USDT Converted: $${data.usdtAmount.toFixed(2)}
- Share Price: $${data.sharePrice.toFixed(2)}
- Shares Received: ${data.sharesReceived}
- Transaction ID: ${data.transactionId}

Your new shares have been added to your commission balance and are now available in your dashboard.

View your dashboard: https://aureus.africa/dashboard

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'conversion'
 });

 console.log('Conversion notification sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending conversion notification:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send commission earned notification
 */
 async sendCommissionEarnedNotification(data: CommissionEarnedData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - commission earned notification not sent');
 return {
 success: false,
 error: 'Email service not configured'
 };
 }

 try {
 const totalCommissionValue = data.usdtCommission + (data.shareCommission * 5); // Estimate share value
 const subject = `Commission Earned - $${data.usdtCommission.toFixed(2)} USDT + ${data.shareCommission} Shares`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">Commission Earned! </h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Great news, ${data.fullName}!</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 You've earned a commission from a successful referral! ${data.referredUserName} just purchased shares, and you've received your referral commission.
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">Commission Details</h3>
 <table style="width: 100%; color: #ffffff;">
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Referred User:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.referredUserName}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Purchase Amount:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">$${data.purchaseAmount.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">USDT Commission:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #3b82f6;">$${data.usdtCommission.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Share Commission:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #d4af37;">${data.shareCommission} shares</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Total Commission Value:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #10b981;">≈$${totalCommissionValue.toFixed(2)}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0;">Transaction ID:</td>
 <td style="padding: 8px 0; text-align: right; font-family: monospace; font-size: 12px;">${data.transactionId}</td>
 </tr>
 </table>
 </div>

 <div style="background-color: #0f3460; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #d4af37;">
 <h4 style="color: #d4af37; margin-top: 0; margin-bottom: 10px;">Your Commission Benefits</h4>
 <ul style="margin: 0; padding-left: 20px; color: #ffffff;">
 <li style="margin-bottom: 8px;"><strong>USDT Commission:</strong> Available for withdrawal or conversion to shares</li>
 <li style="margin-bottom: 8px;"><strong>Share Commission:</strong> Shares credited to your portfolio</li>
 <li style="margin-bottom: 8px;"><strong>Compound Growth:</strong> Convert USDT to more shares</li>
 <li style="margin-bottom: 8px;"><strong>Portfolio:</strong> Shares represent ownership in mining operations</li>
 </ul>
 </div>

 <p style="line-height: 1.6; margin-bottom: 15px;">
 Keep referring friends and family to build your passive income stream. Earn commissions on every successful referral!
 </p>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/affiliate-dashboard#commissions" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block; margin-right: 10px;">
 View Commissions
 </a>
 <a href="https://aureus.africa/affiliate-dashboard#referrals" style="background-color: #0f3460; color: #ffffff; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 Get Referral Link
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
Commission Earned! 

Great news, ${data.fullName}!

You've earned a commission from a successful referral! ${data.referredUserName} just purchased shares, and you've received your referral commission.

Commission Details:
- Referred User: ${data.referredUserName}
- Purchase Amount: $${data.purchaseAmount.toFixed(2)}
- USDT Commission: $${data.usdtCommission.toFixed(2)}
- Share Commission: ${data.shareCommission} shares
- Total Commission Value: ≈$${totalCommissionValue.toFixed(2)}
- Transaction ID: ${data.transactionId}

Your Commission Benefits:
- USDT Commission: Available for withdrawal or conversion to shares
- Share Commission: Shares credited to your portfolio
- Compound Growth: Convert USDT to more shares
- Portfolio: Shares represent ownership in mining operations

Keep referring friends and family to build your passive income stream. Earn commissions on every successful referral!

View your commissions: https://aureus.africa/affiliate-dashboard#commissions

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'commission_earned'
 });

 console.log('Commission earned notification sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending commission earned notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send share transfer notification
 */
 async sendShareTransferNotification(data: ShareTransferNotificationData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - share transfer notification not sent');
 return {
 success: false,
 
 error: 'Email service not configured'
 };
 }

 try {
 const isSender = data.type === 'sender';
 const subject = isSender
 ? `Share Transfer Sent - ${data.shares} Shares Transferred`
 : `Share Transfer Received - ${data.shares} Shares Received`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">
 ${isSender ? 'Share Transfer Sent' : 'Share Transfer Received'} 
 </h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Hello ${data.fullName}!</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 ${isSender
 ? `You have successfully transferred ${data.shares} shares to ${data.recipientName}.`
 : `You have received ${data.shares} shares from ${data.senderName}.`
 }
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">Transfer Details</h3>
 <table style="width: 100%; color: #ffffff;">
 ${isSender ? `
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Recipient:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.recipientName}</td>
 </tr>
 ` : `
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Sender:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.senderName}</td>
 </tr>
 `}
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Shares ${isSender ? 'Transferred' : 'Received'}:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #d4af37;">${data.shares}</td>
 </tr>
 ${isSender && data.transferFee > 0 ? `
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Transfer Fee (2%):</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; color: #f59e0b;">${data.transferFee}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Total Deducted:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold; color: #ef4444;">${data.shares + data.transferFee}</td>
 </tr>
 ` : ''}
 <tr>
 <td style="padding: 8px 0;">Transaction ID:</td>
 <td style="padding: 8px 0; text-align: right; font-family: monospace; font-size: 12px;">${data.transactionId}</td>
 </tr>
 </table>
 </div>

 <div style="background-color: #0f3460; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #d4af37;">
 <h4 style="color: #d4af37; margin-top: 0; margin-bottom: 10px;">
 ${isSender ? 'Transfer Completed' : 'Shares Added to Your Portfolio'}
 </h4>
 <p style="margin: 0; color: #ffffff; line-height: 1.5;">
 ${isSender
 ? `Your shares have been successfully transferred. The recipient has been notified and the shares are now in their portfolio.`
 : `The transferred shares have been added to your portfolio.`
 }
 </p>
 </div>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/affiliate-dashboard#commissions" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 View Your Portfolio
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
${isSender ? 'Share Transfer Sent' : 'Share Transfer Received'}

Hello ${data.fullName}!

${isSender
 ? `You have successfully transferred ${data.shares} shares to ${data.recipientName}.`
 : `You have received ${data.shares} shares from ${data.senderName}.`
}

Transfer Details:
${isSender ? `- Recipient: ${data.recipientName}` : `- Sender: ${data.senderName}`}
- Shares ${isSender ? 'Transferred' : 'Received'}: ${data.shares}
${isSender && data.transferFee > 0 ? `- Transfer Fee (2%): ${data.transferFee}\n- Total Deducted: ${data.shares + data.transferFee}` : ''}
- Transaction ID: ${data.transactionId}

${isSender
 ? 'Your shares have been successfully transferred. The recipient has been notified and the shares are now in their portfolio.'
 : 'The transferred shares have been added to your portfolio.'
}

View your portfolio: https://aureus.africa/affiliate-dashboard#commissions

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'share_transfer'
 });

 console.log('Share transfer notification sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending share transfer notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send migration confirmation email
 */
 async sendMigrationConfirmationEmail(data: MigrationConfirmationData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - migration confirmation not sent');
 return {
 success: false,
 
 error: 'Email service not configured'
 };
 }

 try {
 const subject = `Account Migration Completed - Welcome to Aureus Alliance Holdings Website!`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">Account Migration Successful! </h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Welcome to the Website, ${data.fullName}!</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 Your Telegram account has been successfully migrated to website access! You can now access your Aureus Alliance Holdings account through both Telegram and our website.
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">Your Account Details</h3>
 <table style="width: 100%; color: #ffffff;">
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Website Username:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.username}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Telegram Username:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">${data.telegramUsername}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Email Address:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">${data.email}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0;">Account Status:</td>
 <td style="padding: 8px 0; text-align: right; color: #10b981; font-weight: bold;"> Fully Migrated</td>
 </tr>
 </table>
 </div>

 <div style="background-color: #0f3460; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #d4af37;">
 <h4 style="color: #d4af37; margin-top: 0; margin-bottom: 10px;">What's Available Now</h4>
 <ul style="margin: 0; padding-left: 20px; color: #ffffff;">
 <li style="margin-bottom: 8px;"><strong>Website Access:</strong> Login with your username and password</li>
 <li style="margin-bottom: 8px;"><strong>Telegram Bot:</strong> Continue using the bot as before</li>
 <li style="margin-bottom: 8px;"><strong>Commission Management:</strong> View and withdraw commissions on the website</li>
 <li style="margin-bottom: 8px;"><strong>Share Purchases:</strong> Buy shares through the website interface</li>
 <li style="margin-bottom: 8px;"><strong>Portfolio Tracking:</strong> Monitor your investments</li>
 <li style="margin-bottom: 8px;"><strong>KYC Verification:</strong> Complete verification for withdrawals</li>
 </ul>
 </div>

 <div style="background-color: #065f46; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #10b981;">
 <h4 style="color: #10b981; margin-top: 0; margin-bottom: 10px;">Your Data is Safe</h4>
 <p style="margin: 0; color: #ffffff; line-height: 1.5;">
 All your existing commission balances, referral relationships, and transaction history have been preserved and are now accessible through both platforms.
 </p>
 </div>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/login" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block; margin-right: 10px;">
 Login to Website
 </a>
 <a href="https://aureus.africa/dashboard" style="background-color: #0f3460; color: #ffffff; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 View Dashboard
 </a>
 </div>
 </div>

 <div style="background-color: #2a2a2a; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
 <h3 style="color: #d4af37; margin-top: 0;">Need Help?</h3>
 <p style="color: #ffffff; margin-bottom: 10px;">
 If you have any questions about your migrated account or need assistance:
 </p>
 <ul style="color: #ffffff; margin: 0; padding-left: 20px;">
 <li style="margin-bottom: 5px;">Contact our support team</li>
 <li style="margin-bottom: 5px;">Visit our help center</li>
 <li style="margin-bottom: 5px;">Continue using the Telegram bot for quick access</li>
 </ul>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
Account Migration Successful!

Welcome to the Website, ${data.fullName}!

Your Telegram account has been successfully migrated to website access! You can now access your Aureus Alliance Holdings account through both Telegram and our website.

Your Account Details:
- Website Username: ${data.username}
- Telegram Username: ${data.telegramUsername}
- Email Address: ${data.email}
- Account Status: Fully Migrated

What's Available Now:
- Website Access: Login with your username and password
- Telegram Bot: Continue using the bot as before
- Commission Management: View and withdraw commissions on the website
- Share Purchases: Buy shares through the website interface
- Portfolio Tracking: Monitor your investments
- KYC Verification: Complete verification for withdrawals

Your Data is Safe:
All your existing commission balances, referral relationships, and transaction history have been preserved and are now accessible through both platforms.

Login to website: https://aureus.africa/login
View your dashboard: https://aureus.africa/dashboard

Need Help?
If you have any questions about your migrated account or need assistance:
- Contact our support team
- Visit our help center
- Continue using the Telegram bot for quick access

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'migration_confirmation'
 });

 console.log('Migration confirmation email sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending migration confirmation email:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send new referral notification to sponsor
 */
 async sendNewReferralNotification(data: NewReferralNotificationData): Promise<EmailDeliveryResult> {
 if (!this.isConfigured) {
 console.warn('Resend not configured - new referral notification not sent');
 return {
 success: false,
 
 error: 'Email service not configured'
 };
 }

 try {
 const subject = `New Referral Signup - ${data.newUserName} Joined Your Team!`;

 const htmlContent = `
 <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background-color: #1a1a1a; color: #ffffff;">
 <div style="text-align: center; margin-bottom: 30px;">
 <img src="https://fgubaqoftdeefcakejwu.supabase.co/storage/v1/object/public/assets/logo.png" alt="Aureus Alliance Holdings" style="height: 60px;">
 <h1 style="color: #d4af37; margin: 20px 0;">New Team Member! </h1>
 </div>

 <div style="background-color: #2a2a2a; padding: 25px; border-radius: 8px; margin-bottom: 20px;">
 <h2 style="color: #d4af37; margin-top: 0;">Congratulations, ${data.fullName}!</h2>
 <p style="line-height: 1.6; margin-bottom: 15px;">
 Great news! A new user has joined Aureus Alliance Holdings using your referral link.
 </p>

 <div style="background-color: #1a1a1a; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <h3 style="color: #d4af37; margin-top: 0; margin-bottom: 15px;">New Team Member Details</h3>
 <table style="width: 100%; color: #ffffff;">
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Name:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right; font-weight: bold;">${data.newUserName}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; border-bottom: 1px solid #444;">Email:</td>
 <td style="padding: 8px 0; border-bottom: 1px solid #444; text-align: right;">${data.newUserEmail}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0;">Registration Date:</td>
 <td style="padding: 8px 0; text-align: right;">${new Date(data.registrationDate).toLocaleDateString()}</td>
 </tr>
 </table>
 </div>

 <p style="line-height: 1.6; margin-bottom: 15px;">
 <strong>What happens next?</strong><br>
 When ${data.newUserName} makes their first share purchase, you'll automatically earn:
 </p>

 <div style="background-color: #0f3d0f; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #22c55e;">
 <p style="margin: 0; color: #22c55e; font-weight: bold;">
 10% USDT Commission + Automatic 5% Share Purchase
 </p>
 </div>

 <p style="line-height: 1.6; margin-bottom: 15px;">
 Keep referring friends and earn commissions on every direct referral!
 </p>

 <div style="text-align: center; margin: 25px 0;">
 <a href="https://aureus.africa/dashboard?section=referrals" style="background-color: #d4af37; color: #000000; padding: 12px 30px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
 View Your Referrals
 </a>
 </div>
 </div>

 <div style="text-align: center; font-size: 14px; color: #888888; margin-top: 30px;">
 <p>Real Gold • Real Shares • Real Ownership</p>
 <p>Aureus Alliance Holdings (Pty) Ltd</p>
 <p>Building Gold-Backed Impact Ventures Across Africa</p>
 </div>
 </div>
 `;

 const textContent = `
New Team Member!

Congratulations, ${data.fullName}!

Great news! A new user has joined Aureus Alliance Holdings using your referral link.

New Team Member Details:
- Name: ${data.newUserName}
- Email: ${data.newUserEmail}
- Registration Date: ${new Date(data.registrationDate).toLocaleDateString()}

What happens next?
When ${data.newUserName} makes their first share purchase, you'll automatically earn:
 10% USDT Commission + Automatic 5% Share Purchase

Keep referring friends and earn commissions on every direct referral!

View your referrals: https://aureus.africa/dashboard?section=referrals

Real Gold • Real Shares • Real Ownership
Aureus Alliance Holdings (Pty) Ltd
Building Gold-Backed Impact Ventures Across Africa
 `.trim();

 const result = await this.sendEmail({
 to: data.email,
 subject,
 html: htmlContent,
 text: textContent,
 // emailType: 'new_referral'
 });

 console.log('New referral notification sent successfully to:', data.email);
 return result;

 } catch (error) {
 console.error('Error sending new referral notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send withdrawal request notification email
 */
 async sendWithdrawalRequestNotification(
 email: string,
 userName: string,
 amount: number,
 currency: string,
 walletAddress: string,
 network?: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending withdrawal request notification to:', email);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('withdrawal_request', {
 email,
 userName,
 amount,
 walletAddress,
 transactionId: `WD-${Date.now()}`
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Withdrawal Request Submitted - ${amount.toFixed(2)} ${currency}`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Request Submitted</title>
 <style>
 body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 0; background-color: #f4f4f4; }
 .container { max-width: 600px; margin: 0 auto; background: white; padding: 20px; border-radius: 10px; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
 .header { background: linear-gradient(135deg, #ffc107, #ff8f00); color: white; padding: 30px 20px; text-align: center; border-radius: 10px 10px 0 0; margin: -20px -20px 20px -20px; }
 .header h1 { margin: 0; font-size: 28px; }
 .content { padding: 20px 0; }
 .withdrawal-details { background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ffc107; }
 .detail-row { display: flex; justify-content: space-between; margin: 10px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
 .detail-label { font-weight: bold; color: #666; }
 .detail-value { color: #333; }
 .status-badge { background: #fff3cd; color: #856404; padding: 8px 16px; border-radius: 20px; font-weight: bold; text-align: center; margin: 20px 0; }
 .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
 .warning { background: #fff3cd; border: 1px solid #ffeaa7; color: #856404; padding: 15px; border-radius: 8px; margin: 20px 0; }
 .button { display: inline-block; background: #ffc107; color: #000; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: bold; margin: 20px 0; }
 </style>
 </head>
 <body>
 <div class="container">
 <div class="header">
 <h1> Withdrawal Request Submitted</h1>
 <p>Your withdrawal request has been received</p>
 </div>

 <div class="content">
 <p>Hello <strong>${userName}</strong>,</p>

 <p>We have received your withdrawal request and it is now being processed by our team.</p>

 <div class="withdrawal-details">
 <h3> Withdrawal Details</h3>
 <div class="detail-row">
 <span class="detail-label">Amount:</span>
 <span class="detail-value">${amount.toFixed(2)} ${currency}</span>
 </div>
 <div class="detail-row">
 <span class="detail-label">Wallet Address:</span>
 <span class="detail-value" style="font-family: monospace; font-size: 12px;">${walletAddress}</span>
 </div>
 ${network ? `
 <div class="detail-row">
 <span class="detail-label">Network:</span>
 <span class="detail-value">${network}</span>
 </div>
 ` : ''}
 <div class="detail-row">
 <span class="detail-label">Request Date:</span>
 <span class="detail-value">${new Date().toLocaleDateString()}</span>
 </div>
 </div>

 <div class="status-badge">
 ⏳ Status: PENDING APPROVAL
 </div>

 <div class="warning">
 <strong> Important:</strong> Your withdrawal request is now pending admin approval. You will receive another email once your request has been processed. Processing typically takes 1-3 business days.
 </div>

 <p>If you have any questions about your withdrawal, please contact our support team.</p>
 </div>

 <div class="footer">
 <p>Best regards,<br><strong>Aureus Alliance Holdings Team</strong></p>
 <p style="font-size: 12px; color: #999;">
 This is an automated message. Please do not reply to this email.
 </p>
 </div>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 Withdrawal Request Submitted

 Hello ${userName},

 We have received your withdrawal request and it is now being processed by our team.

 Withdrawal Details:
 - Amount: ${amount.toFixed(2)} ${currency}
 - Wallet Address: ${walletAddress}
 ${network ? `- Network: ${network}` : ''}
 - Request Date: ${new Date().toLocaleDateString()}

 Status: PENDING APPROVAL

 Your withdrawal request is now pending admin approval. You will receive another email once your request has been processed. Processing typically takes 1-3 business days.

 If you have any questions about your withdrawal, please contact our support team.

 Best regards,
 Aureus Alliance Holdings Team
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Withdrawal request notification sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending withdrawal request notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send admin withdrawal alert email
 */
 async sendAdminWithdrawalAlert(
 userName: string,
 userId: number,
 amount: number,
 walletAddress: string,
 network: string,
 withdrawalId: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending admin withdrawal alert for:', userName);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('withdrawal_request', {
 email: 'accounts@aureus.africa',
 userName,
 amount,
 walletAddress,
 transactionId: withdrawalId
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `New Withdrawal Request - ${userName} - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>New Withdrawal Request</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;"> New Withdrawal Request</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #f59e0b;">
 <h2 style="color: #f59e0b; margin-top: 0;">Withdrawal Details</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">User:</td>
 <td style="padding: 8px 0;">${userName} (ID: ${userId})</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #f59e0b;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Wallet Address:</td>
 <td style="padding: 8px 0; font-family: monospace; background: #f8f9fa; padding: 5px; border-radius: 4px;">${walletAddress}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Network:</td>
 <td style="padding: 8px 0;">${network}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Request ID:</td>
 <td style="padding: 8px 0; font-family: monospace; font-size: 12px;">${withdrawalId}</td>
 </tr>
 </table>
 </div>

 <div style="text-align: center; margin: 30px 0;">
 <a href="https://aureus.africa/admin" style="background: #f59e0b; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
 Process Withdrawal
 </a>
 </div>

 <div style="background: #fff3cd; border: 1px solid #ffeaa7; padding: 15px; border-radius: 8px; margin-top: 20px;">
 <p style="margin: 0; color: #856404;"><strong> Action Required:</strong> This withdrawal request requires admin approval. Please review and process through the admin dashboard.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated notification from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 NEW WITHDRAWAL REQUEST

 User: ${userName} (ID: ${userId})
 Amount: $${amount.toFixed(2)} USDT
 Wallet Address: ${walletAddress}
 Network: ${network}
 Request ID: ${withdrawalId}

 Action Required: Please review and process this withdrawal request through the admin dashboard.

 Admin Dashboard: https://aureus.africa/admin

 This is an automated notification from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: ['accounts@aureus.africa'],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Admin withdrawal alert sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending admin withdrawal alert:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send withdrawal proof notification to user
 */
 async sendWithdrawalProofNotification(
 email: string,
 userName: string,
 amount: number,
 proofUrl: string,
 transactionHash: string,
 withdrawalId: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending withdrawal proof notification to:', email);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('custom', {
 email,
 subject: `Withdrawal Processed - Please Confirm Receipt - $${amount.toFixed(2)} USDT`,
 htmlContent: `<p>Your withdrawal of $${amount.toFixed(2)} USDT has been processed. Transaction: ${transactionHash}</p>`,
 textContent: `Your withdrawal of $${amount.toFixed(2)} USDT has been processed. Transaction: ${transactionHash}`
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Withdrawal Processed - Please Confirm Receipt - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Processed</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;"> Withdrawal Processed!</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <p style="font-size: 18px; margin-bottom: 25px;">Hello ${userName},</p>

 <p style="font-size: 16px; margin-bottom: 25px;">Great news! Your withdrawal request has been processed and the funds have been sent to your wallet.</p>

 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #10b981;">
 <h2 style="color: #10b981; margin-top: 0;">Transaction Details</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #10b981;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Transaction Hash:</td>
 <td style="padding: 8px 0; font-family: monospace; background: #f8f9fa; padding: 5px; border-radius: 4px; word-break: break-all;">${transactionHash}</td>
 </tr>
 </table>
 </div>

 <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 25px; text-align: center;">
 <h3 style="color: #f59e0b; margin-top: 0;"> Proof of Payment</h3>
 <p style="margin-bottom: 15px;">Click below to view the proof of payment:</p>
 <a href="${proofUrl}" target="_blank" style="background: #f59e0b; color: white; padding: 12px 25px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
 View Proof of Payment
 </a>
 </div>

 <div style="text-align: center; margin: 30px 0;">
 <a href="https://aureus.africa/dashboard?confirm=${withdrawalId}" style="background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block; font-size: 16px;">
 Confirm Receipt
 </a>
 </div>

 <div style="background: #d1ecf1; border: 1px solid #bee5eb; padding: 15px; border-radius: 8px; margin-top: 25px;">
 <p style="margin: 0; color: #0c5460;"><strong>⏰ Important:</strong> Please confirm receipt of your withdrawal by clicking the button above. This helps us complete the transaction process.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated notification from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 WITHDRAWAL PROCESSED!

 Hello ${userName},

 Great news! Your withdrawal request has been processed and the funds have been sent to your wallet.

 Transaction Details:
 - Amount: $${amount.toFixed(2)} USDT
 - Transaction Hash: ${transactionHash}

 Proof of Payment: ${proofUrl}

 IMPORTANT: Please confirm receipt of your withdrawal by visiting:
 https://aureus.africa/dashboard?confirm=${withdrawalId}

 This helps us complete the transaction process.

 This is an automated notification from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Withdrawal proof notification sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending withdrawal proof notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send withdrawal rejection notification to user
 */
 async sendWithdrawalRejectionNotification(
 email: string,
 userName: string,
 amount: number,
 rejectionReason: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending withdrawal rejection notification to:', email);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('withdrawal_rejection', {
 email,
 userName,
 amount,
 rejectionReason
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Withdrawal Request Rejected - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Request Rejected</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;"> Withdrawal Request Rejected</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <p style="font-size: 18px; margin-bottom: 25px;">Hello ${userName},</p>

 <p style="font-size: 16px; margin-bottom: 25px;">We regret to inform you that your withdrawal request has been rejected.</p>

 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #ef4444;">
 <h2 style="color: #ef4444; margin-top: 0;">Rejection Details</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #ef4444;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666; vertical-align: top;">Reason:</td>
 <td style="padding: 8px 0; background: #fef2f2; padding: 10px; border-radius: 4px; border: 1px solid #fecaca;">${rejectionReason}</td>
 </tr>
 </table>
 </div>

 <div style="background: #fef3c7; border: 1px solid #fde68a; padding: 20px; border-radius: 8px; margin-bottom: 25px;">
 <h3 style="color: #92400e; margin-top: 0;"> Your Funds Are Safe</h3>
 <p style="margin: 0; color: #92400e;">The requested amount has been returned to your available balance. You can submit a new withdrawal request once the issue has been resolved.</p>
 </div>

 <div style="text-align: center; margin: 30px 0;">
 <a href="https://aureus.africa/dashboard" style="background: #f59e0b; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
 View Dashboard
 </a>
 </div>

 <div style="background: #e0f2fe; border: 1px solid #b3e5fc; padding: 15px; border-radius: 8px; margin-top: 25px;">
 <p style="margin: 0; color: #01579b;"><strong> Need Help?</strong> If you have questions about this rejection, please contact our support team for assistance.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated notification from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 WITHDRAWAL REQUEST REJECTED

 Hello ${userName},

 We regret to inform you that your withdrawal request has been rejected.

 Rejection Details:
 - Amount: $${amount.toFixed(2)} USDT
 - Reason: ${rejectionReason}

 Your Funds Are Safe:
 The requested amount has been returned to your available balance. You can submit a new withdrawal request once the issue has been resolved.

 Dashboard: https://aureus.africa/dashboard

 Need Help? If you have questions about this rejection, please contact our support team for assistance.

 This is an automated notification from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Withdrawal rejection notification sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending withdrawal rejection notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send withdrawal completion notification to user
 */
 async sendWithdrawalCompletionNotification(
 email: string,
 userName: string,
 amount: number,
 transactionHash: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending withdrawal completion notification to:', email);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('custom', {
 email,
 subject: `Withdrawal Complete - $${amount.toFixed(2)} USDT`,
 htmlContent: `<p>Your withdrawal of $${amount.toFixed(2)} USDT has been completed. Transaction: ${transactionHash}</p>`,
 textContent: `Your withdrawal of $${amount.toFixed(2)} USDT has been completed. Transaction: ${transactionHash}`
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Withdrawal Complete - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Complete</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;"> Withdrawal Complete!</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <p style="font-size: 18px; margin-bottom: 25px;">Hello ${userName},</p>

 <p style="font-size: 16px; margin-bottom: 25px;">Excellent! Your withdrawal has been successfully completed. Thank you for confirming receipt.</p>

 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #10b981;">
 <h2 style="color: #10b981; margin-top: 0;">Final Transaction Summary</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #10b981;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Transaction Hash:</td>
 <td style="padding: 8px 0; font-family: monospace; background: #f8f9fa; padding: 5px; border-radius: 4px; word-break: break-all;">${transactionHash}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Status:</td>
 <td style="padding: 8px 0; color: #10b981; font-weight: bold;"> COMPLETED</td>
 </tr>
 </table>
 </div>

 <div style="text-align: center; margin: 30px 0;">
 <a href="https://aureus.africa/dashboard" style="background: #f59e0b; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
 View Dashboard
 </a>
 </div>

 <div style="background: #d1f2eb; border: 1px solid #a7f3d0; padding: 20px; border-radius: 8px; margin-top: 25px;">
 <h3 style="color: #065f46; margin-top: 0;"> Thank You!</h3>
 <p style="margin: 0; color: #065f46;">Thank you for using Aureus Alliance Holdings. We appreciate your business and look forward to serving you again.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated notification from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 WITHDRAWAL COMPLETE!

 Hello ${userName},

 Excellent! Your withdrawal has been successfully completed. Thank you for confirming receipt.

 Final Transaction Summary:
 - Amount: $${amount.toFixed(2)} USDT
 - Transaction Hash: ${transactionHash}
 - Status: COMPLETED

 Dashboard: https://aureus.africa/dashboard

 Thank You!
 Thank you for using Aureus Alliance Holdings. We appreciate your business and look forward to serving you again.

 This is an automated notification from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Withdrawal completion notification sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending withdrawal completion notification:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send admin withdrawal confirmation email
 */
 async sendAdminWithdrawalConfirmation(
 userName: string,
 userId: number,
 amount: number,
 transactionHash: string
 ): Promise<EmailResult> {
 try {
 console.log('Sending admin withdrawal confirmation for:', userName);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('custom', {
 email: 'accounts@aureus.africa',
 subject: `Withdrawal Confirmed by User - ${userName} - $${amount.toFixed(2)} USDT`,
 htmlContent: `<p>User ${userName} (ID: ${userId}) has confirmed receipt of $${amount.toFixed(2)} USDT. Transaction: ${transactionHash}</p>`,
 textContent: `User ${userName} (ID: ${userId}) has confirmed receipt of $${amount.toFixed(2)} USDT. Transaction: ${transactionHash}`
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Withdrawal Confirmed by User - ${userName} - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Confirmed</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #10b981 0%, #059669 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;"> Withdrawal Confirmed</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <p style="font-size: 16px; margin-bottom: 25px;">The user has confirmed receipt of their withdrawal. The transaction is now complete.</p>

 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 20px; border-left: 4px solid #10b981;">
 <h2 style="color: #10b981; margin-top: 0;">Completed Transaction</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">User:</td>
 <td style="padding: 8px 0;">${userName} (ID: ${userId})</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #10b981;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Transaction Hash:</td>
 <td style="padding: 8px 0; font-family: monospace; background: #f8f9fa; padding: 5px; border-radius: 4px; word-break: break-all;">${transactionHash}</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Status:</td>
 <td style="padding: 8px 0; color: #10b981; font-weight: bold;"> COMPLETED & CONFIRMED</td>
 </tr>
 </table>
 </div>

 <div style="background: #d1f2eb; border: 1px solid #a7f3d0; padding: 15px; border-radius: 8px; margin-top: 20px;">
 <p style="margin: 0; color: #065f46;"><strong> Transaction Complete:</strong> The escrow has been cleared and the withdrawal is fully processed.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated notification from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 WITHDRAWAL CONFIRMED

 The user has confirmed receipt of their withdrawal. The transaction is now complete.

 Completed Transaction:
 - User: ${userName} (ID: ${userId})
 - Amount: $${amount.toFixed(2)} USDT
 - Transaction Hash: ${transactionHash}
 - Status: COMPLETED & CONFIRMED

 Transaction Complete: The escrow has been cleared and the withdrawal is fully processed.

 This is an automated notification from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: ['accounts@aureus.africa'],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Admin withdrawal confirmation sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending admin withdrawal confirmation:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send withdrawal confirmation reminder email
 */
 async sendWithdrawalConfirmationReminder(
 email: string,
 userName: string,
 amount: number,
 proofUrl: string,
 transactionHash: string,
 withdrawalId: string,
 daysSinceProcessed: number
 ): Promise<EmailResult> {
 try {
 console.log('Sending withdrawal confirmation reminder to:', email);

 // Use API proxy if in browser
 if (IS_BROWSER) {
 const result = await this.sendViaAPI('custom', {
 email,
 subject: `Reminder: Please Confirm Your Withdrawal - $${amount.toFixed(2)} USDT`,
 htmlContent: `<p>Reminder: Your withdrawal of $${amount.toFixed(2)} USDT was processed ${daysSinceProcessed} days ago. Please confirm receipt.</p>`,
 textContent: `Reminder: Your withdrawal of $${amount.toFixed(2)} USDT was processed ${daysSinceProcessed} days ago. Please confirm receipt.`
 });
 return { success: result.success, messageId: result.messageId, error: result.error };
 }

 if (!resend) {
 throw new Error('Resend service not initialized');
 }

 const subject = `Reminder: Please Confirm Your Withdrawal - $${amount.toFixed(2)} USDT`;

 const htmlContent = `
 <!DOCTYPE html>
 <html>
 <head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>Withdrawal Confirmation Reminder</title>
 </head>
 <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
 <h1 style="color: white; margin: 0; font-size: 28px;">⏰ Confirmation Reminder</h1>
 </div>

 <div style="background: #f8f9fa; padding: 30px; border-radius: 0 0 10px 10px; border: 1px solid #e9ecef;">
 <p style="font-size: 18px; margin-bottom: 25px;">Hello ${userName},</p>

 <p style="font-size: 16px; margin-bottom: 25px;">This is a friendly reminder that your withdrawal was processed ${daysSinceProcessed} day${daysSinceProcessed > 1 ? 's' : ''} ago and is awaiting your confirmation.</p>

 <div style="background: white; padding: 25px; border-radius: 8px; margin-bottom: 25px; border-left: 4px solid #f59e0b;">
 <h2 style="color: #f59e0b; margin-top: 0;">Transaction Details</h2>
 <table style="width: 100%; border-collapse: collapse;">
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Amount:</td>
 <td style="padding: 8px 0; font-size: 18px; font-weight: bold; color: #f59e0b;">$${amount.toFixed(2)} USDT</td>
 </tr>
 <tr>
 <td style="padding: 8px 0; font-weight: bold; color: #666;">Transaction Hash:</td>
 <td style="padding: 8px 0; font-family: monospace; background: #f8f9fa; padding: 5px; border-radius: 4px; word-break: break-all;">${transactionHash}</td>
 </tr>
 </table>
 </div>

 <div style="background: white; padding: 20px; border-radius: 8px; margin-bottom: 25px; text-align: center;">
 <h3 style="color: #f59e0b; margin-top: 0;"> Proof of Payment</h3>
 <p style="margin-bottom: 15px;">Click below to view the proof of payment:</p>
 <a href="${proofUrl}" target="_blank" style="background: #f59e0b; color: white; padding: 12px 25px; text-decoration: none; border-radius: 6px; font-weight: bold; display: inline-block;">
 View Proof of Payment
 </a>
 </div>

 <div style="text-align: center; margin: 30px 0;">
 <a href="https://aureus.africa/dashboard?confirm=${withdrawalId}" style="background: #10b981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block; font-size: 16px;">
 Confirm Receipt Now
 </a>
 </div>

 <div style="background: #fff3cd; border: 1px solid #ffeaa7; padding: 20px; border-radius: 8px; margin-top: 25px;">
 <h3 style="color: #92400e; margin-top: 0;"> Action Required</h3>
 <p style="margin: 0; color: #92400e;">Please confirm receipt of your withdrawal to complete the transaction process. If you haven't received the funds, please contact our support team.</p>
 </div>
 </div>

 <div style="text-align: center; margin-top: 30px; padding: 20px; color: #666; font-size: 12px;">
 <p>This is an automated reminder from Aureus Alliance Holdings</p>
 <p>© 2024 Aureus Alliance Holdings (Pty) Ltd. All rights reserved.</p>
 </div>
 </body>
 </html>
 `;

 const textContent = `
 ⏰ WITHDRAWAL CONFIRMATION REMINDER

 Hello ${userName},

 This is a friendly reminder that your withdrawal was processed ${daysSinceProcessed} day${daysSinceProcessed > 1 ? 's' : ''} ago and is awaiting your confirmation.

 Transaction Details:
 - Amount: $${amount.toFixed(2)} USDT
 - Transaction Hash: ${transactionHash}

 Proof of Payment: ${proofUrl}

 IMPORTANT: Please confirm receipt of your withdrawal by visiting:
 https://aureus.africa/dashboard?confirm=${withdrawalId}

 If you haven't received the funds, please contact our support team.

 This is an automated reminder from Aureus Alliance Holdings.
 `;

 const result = await resend.emails.send({
 from: `${RESEND_FROM_NAME} <${RESEND_FROM_EMAIL}>`,
 to: [email],
 subject,
 html: htmlContent,
 text: textContent,
 replyTo: 'support@aureus.africa',
 headers: {
 'List-Unsubscribe': '<mailto:unsubscribe@aureus.africa>'
 },
 });

 console.log('Withdrawal confirmation reminder sent successfully:', result.data?.id);

 return {
 success: true,
 messageId: result.data?.id || undefined,
 error: undefined
 };

 } catch (error) {
 console.error('Error sending withdrawal confirmation reminder:', error);
 return {
 success: false,
 
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Strip HTML tags for text content
 */
 private stripHtml(html: string): string {
 return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
 }
}

// Export singleton instance
export const resendEmailService = new ResendEmailService();
export default resendEmailService;


